import {
  Box,
  CircularProgress,
  CircularProgressProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { memo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { useTranslation } from '../modules/translation/TranslationContext';
import useTeceoStyles from '../useTeceoStyles';
import useWidth from './Hooks/useWidth';
import { formatProgressLabel } from './v2/ui/Progress/progress.utils';

type ConversionRingTextProps = Pick<TypographyProps, 'variant' | 'className'>;

type ConversionRingProps = {
  value: number;
  opportunityValue?: number;
  showTextLeftSide?: boolean;
  showTextRightSide?: boolean;
  horizontalSpacing?: number;
  textProps?: ConversionRingTextProps;
} & CircularProgressProps;

interface UseStylesProps {
  showRightSide: boolean;
  conversionSize: number;
}

interface ConversionTextProps {
  showTextLeftSide?: boolean;
  conversionValue: string;
  textProps?: ConversionRingTextProps;
  showTextRightSide?: boolean;
  isOpportunityValueInvalid: boolean;
}

const useConversionRingsStyles = makeStyles<UseStylesProps>()(
  (theme, { conversionSize, showRightSide }) => ({
    bottom: {
      color: theme.palette.grey[300],
    },
    top: {
      position: 'absolute',
      left: showRightSide ? undefined : 0,
      right: showRightSide ? 0 : undefined,
    },
    circle: {
      strokeLinecap: 'round',
    },
    whiteSpace: {
      whiteSpace: 'nowrap',
    },
    textFontSize: {
      fontSize: conversionSize > 4 ? '11px' : undefined,
    },
  }),
);

const ConversionText = ({
  conversionValue,
  isOpportunityValueInvalid,
  showTextLeftSide,
  showTextRightSide,
  textProps,
}: ConversionTextProps) => {
  const { classes, cx } = useConversionRingsStyles({
    showRightSide: !!showTextLeftSide,
    conversionSize: conversionValue.length,
  });
  const { classes: teceoClasses } = useTeceoStyles();

  return (
    <Box
      sx={{
        top: 0,
        left: showTextRightSide ? 5 : 0,
        bottom: 0,
        right: 0,
        position: showTextRightSide || showTextLeftSide ? 'relative' : 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant={textProps?.variant ?? 'caption'}
        component="div"
        color="textPrimary"
        className={cx(
          teceoClasses.boldText,
          teceoClasses.secondaryTypography,
          classes.whiteSpace,
          textProps?.className,
          classes.textFontSize,
        )}
      >
        {isOpportunityValueInvalid ? '-' : conversionValue}
      </Typography>
    </Box>
  );
};

const ConversionRing = ({
  value,
  horizontalSpacing,
  textProps,
  showTextLeftSide,
  showTextRightSide,
  opportunityValue,
  ...rest
}: ConversionRingProps) => {
  const { smallWidth } = useWidth();
  const { locale } = useTranslation();
  const conversionValue = formatProgressLabel(value, locale);
  const { classes, cx } = useConversionRingsStyles({
    showRightSide: !!showTextLeftSide,
    conversionSize: conversionValue.length,
  });
  const { classes: teceoClasses } = useTeceoStyles();

  const isOpportunityValueInvalid = !!opportunityValue && opportunityValue <= 0;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        columnGap: horizontalSpacing ? `${horizontalSpacing}px` : undefined,
      }}
    >
      {showTextLeftSide && (
        <ConversionText
          conversionValue={conversionValue}
          isOpportunityValueInvalid={isOpportunityValueInvalid}
          showTextLeftSide={showTextLeftSide}
          showTextRightSide={showTextRightSide}
          textProps={textProps}
        />
      )}
      <CircularProgress
        variant="determinate"
        className={cx({
          [teceoClasses.secondary]: value > 100,
          [classes.bottom]: value <= 100,
        })}
        size={smallWidth ? 40 : 44}
        {...rest}
        value={100}
      />
      <CircularProgress
        variant="determinate"
        className={cx(classes.top, teceoClasses.tertiary)}
        size={smallWidth ? 40 : 44}
        {...rest}
        value={value > 100 ? 10000 / value : value}
      />
      {!showTextLeftSide && (
        <ConversionText
          conversionValue={conversionValue}
          isOpportunityValueInvalid={isOpportunityValueInvalid}
          showTextLeftSide={showTextLeftSide}
          showTextRightSide={showTextRightSide}
          textProps={textProps}
        />
      )}
    </Box>
  );
};

/** @deprecated Use `components/v2/ui/Progress` instead. */
export default memo(ConversionRing);
